import { Link, Routes, Route, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import type { ElementType } from 'react';
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import CoursesIcon from '@mui/icons-material/MenuBook';
import TenantsIcon from '@mui/icons-material/Business';
import SettingsIcon from '@mui/icons-material/Settings';
import ReportsIcon from '@mui/icons-material/Assessment';
import SubscriptionsIcon from '@mui/icons-material/CardMembership';
import ImportIcon from '@mui/icons-material/CloudDownload';
import SearchIndexIcon from '@mui/icons-material/ManageSearch';
import MemoryMigrationIcon from '@mui/icons-material/CloudSync';
import CpeRequirementsIcon from '@mui/icons-material/FactCheck';
import IntegrationsHealthIcon from '@mui/icons-material/MonitorHeart';
import WebhookEventsIcon from '@mui/icons-material/Webhook';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AdminNavRow from '../../components/AdminNavRow';
import Tenants from './Tenants';
import TenantDetail from './TenantDetail';
import TenantCreditImport from './TenantCreditImport';
import Subscriptions from './Subscriptions';
import GlobalCourses from './GlobalCourses';
import SystemSettings from './SystemSettings';
import DoceboCourseImport from './DoceboCourseImport';
import WebinarCourseImport from './WebinarCourseImport';
import SearchIndexManagement from './SearchIndexManagement';
import SystemReports from './SystemReports';
import MemoryMigration from './MemoryMigration';
import CpeRequirementImports from './CpeRequirementImports';
import IntegrationsHealth from './IntegrationsHealth';
import WebhookEvents from './WebhookEvents';
import { UserManagement } from '../tenant-admin';
import userService from '../../services/userService';
import courseService from '../../services/courseService';
import cpeRequirementImportService from '../../services/cpeRequirementImportService';
import adminService from '../../services/adminService';
import webhookEventService from '../../services/webhookEventService';

interface NavItem {
  to: string;
  icon: ElementType;
  title: string;
  desc: string;
  countKey?: 'users' | 'courses';
  badgeKey?: 'monitorFindings' | 'integrationFailures' | 'webhookFailures';
}

const NAV_GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: 'People & Organizations',
    items: [
      { to: 'users', icon: PeopleIcon, title: 'All Users', desc: 'User accounts across all tenants', countKey: 'users' },
      { to: 'tenants', icon: TenantsIcon, title: 'Tenants', desc: 'Tenant organizations' },
      { to: 'subscriptions', icon: SubscriptionsIcon, title: 'Subscriptions', desc: 'Provisioning and trial periods' },
    ],
  },
  {
    label: 'Course Catalog',
    items: [
      { to: 'courses', icon: CoursesIcon, title: 'Global Courses', desc: 'System-wide course management', countKey: 'courses' },
      { to: 'docebo-import', icon: ImportIcon, title: 'Docebo Course Sync', desc: 'Import from the Docebo catalog' },
      { to: 'webinar-import', icon: ImportIcon, title: 'Webinar Course Sync', desc: 'Import webinars from the CPAx source' },
      { to: 'search-index', icon: SearchIndexIcon, title: 'Course Search Index', desc: 'Rebuild the Meilisearch index' },
    ],
  },
  {
    label: 'Platform',
    items: [
      { to: 'settings', icon: SettingsIcon, title: 'System Settings', desc: 'Global application configuration' },
      { to: 'reports', icon: ReportsIcon, title: 'System Reports', desc: 'Platform-wide analytics' },
      { to: 'cpe-requirements', icon: CpeRequirementsIcon, title: 'CPE Requirements Data', desc: 'Import status and verification by state', badgeKey: 'monitorFindings' },
      { to: 'integrations', icon: IntegrationsHealthIcon, title: 'Integrations Health', desc: 'Status of external service connections', badgeKey: 'integrationFailures' },
      { to: 'webhooks', icon: WebhookEventsIcon, title: 'Webhook Events', desc: 'Chargebee, Docebo and monitor deliveries', badgeKey: 'webhookFailures' },
      { to: 'memories', icon: MemoryMigrationIcon, title: 'Migrate Memories', desc: 'Move Vertex AI memories between engines' },
    ],
  },
];

const SUB_PAGE_LABELS: Record<string, string> = {
  tenants: 'Tenants',
  subscriptions: 'Subscriptions',
  courses: 'Global Courses',
  settings: 'System Settings',
  'docebo-import': 'Docebo Course Sync',
  'webinar-import': 'Webinar Course Sync',
  'search-index': 'Course Search Index',
  memories: 'Migrate Memories',
  users: 'User Management',
  reports: 'System Reports',
  'cpe-requirements': 'CPE Requirements Data',
  integrations: 'Integrations Health',
  webhooks: 'Webhook Events',
};

export default function AdminLayout() {
  const location = useLocation();
  const [courseCount, setCourseCount] = useState<number | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [monitorFindingsCount, setMonitorFindingsCount] = useState<number | null>(null);
  const [integrationFailuresCount, setIntegrationFailuresCount] = useState<number | null>(null);
  const [webhookFailuresCount, setWebhookFailuresCount] = useState<number | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);

  const basePath = '/app/system-admin';
  const subSegment = location.pathname.replace(basePath, '').replace(/^\//, '').split('/')[0];
  const isSubPage = Boolean(subSegment);
  const subPageLabel = SUB_PAGE_LABELS[subSegment] ?? subSegment;

  useEffect(() => {
    async function fetchDashboardData() {
      try {
        const [coursesRes, usersData, monitorStatusRes] = await Promise.allSettled([
          courseService.getCourses(),
          userService.getAllSystemUsers(),
          cpeRequirementImportService.getMonitorStatus(),
        ]);
        if (coursesRes.status === 'fulfilled') {
          setCourseCount(coursesRes.value.length);
        }
        if (usersData.status === 'fulfilled') {
          setUsers(usersData.value);
        }
        if (monitorStatusRes.status === 'fulfilled') {
          setMonitorFindingsCount(monitorStatusRes.value.unreviewedCount);
        }
      } finally {
        setLoadingStats(false);
      }
    }
    fetchDashboardData();
    // Fetched separately: health probes hit external services and can be slow,
    // so they must not delay the user/course counts above.
    adminService
      .getIntegrationsHealth()
      .then((health) => setIntegrationFailuresCount(health.filter((i) => i.status === 'unhealthy').length))
      .catch(() => setIntegrationFailuresCount(null));
    // Failed webhook deliveries across all time — they stay failed until someone retries them.
    webhookEventService
      .getStats(24)
      .then((stats) => setWebhookFailuresCount(stats.failedTotal))
      .catch(() => setWebhookFailuresCount(null));
  }, []);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumb — only on sub-pages */}
      {isSubPage && (
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Typography
            component={Link}
            to={basePath}
            sx={{ fontSize: 14, color: 'text.secondary', textDecoration: 'none', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
          >
            System Administration
          </Typography>
          <Typography sx={{ fontSize: 14, color: 'text.primary', fontWeight: 500 }}>
            {subPageLabel}
          </Typography>
        </Breadcrumbs>
      )}

      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={500} gutterBottom>
          {isSubPage ? subPageLabel : 'System Administration'}
        </Typography>
        {!isSubPage && (
          <Typography variant="body1" color="text.secondary">
            Manage all tenants, users, and system-wide configuration
          </Typography>
        )}
      </Box>

      <Routes>
        <Route path="tenants" element={<Tenants />} />
        <Route path="tenants/:tenantId" element={<TenantDetail />} />
        <Route path="tenants/:tenantId/import-credits" element={<TenantCreditImport />} />
        <Route path="subscriptions" element={<Subscriptions />} />
        <Route path="courses" element={<GlobalCourses />} />
        <Route path="settings" element={<SystemSettings />} />
        <Route path="docebo-import" element={<DoceboCourseImport />} />
        <Route path="webinar-import" element={<WebinarCourseImport />} />
        <Route path="search-index" element={<SearchIndexManagement />} />
        <Route path="memories" element={<MemoryMigration />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="reports" element={<SystemReports />} />
        <Route path="cpe-requirements" element={<CpeRequirementImports />} />
        <Route path="integrations" element={<IntegrationsHealth />} />
        <Route path="webhooks" element={<WebhookEvents />} />
        <Route
          index
          element={
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                columnGap: { md: 4, lg: 7 },
                rowGap: 5,
                alignItems: 'start',
              }}
            >
              {NAV_GROUPS.map((group) => (
                <Box key={group.label}>
                  <Typography
                    variant="overline"
                    component="h2"
                    sx={{ display: 'block', color: 'text.secondary', px: 1.5, mb: 0.5, lineHeight: 2 }}
                  >
                    {group.label}
                  </Typography>
                  <Box component="nav" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {group.items.map((item) => (
                      <AdminNavRow
                        key={item.to}
                        to={`${basePath}/${item.to}`}
                        icon={item.icon}
                        title={item.title}
                        desc={item.desc}
                        count={
                          loadingStats
                            ? null
                            : item.countKey === 'users'
                              ? users.length
                              : item.countKey === 'courses'
                                ? courseCount
                                : null
                        }
                        badgeCount={
                          item.badgeKey === 'monitorFindings'
                            ? monitorFindingsCount
                            : item.badgeKey === 'integrationFailures'
                              ? integrationFailuresCount
                              : item.badgeKey === 'webhookFailures'
                                ? webhookFailuresCount
                                : null
                        }
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          }
        />
      </Routes>
    </Container>
  );
}

